import { create } from 'zustand';

const ACTIVE_KEY = 'redcoral-active-account';
const ACCOUNTS_KEY = 'redcoral-accounts';

export interface SavedAccount {
  tenant_id: string;
  email: string;
  name?: string;
  token: string;
  refresh_token: string;
}

interface AuthStore {
  token: string | null;
  refreshToken: string | null;
  tenantId: string | null;
  email: string | null;
  name: string | null;
  isAuthenticated: boolean;
  accounts: SavedAccount[];

  setAuth: (account: SavedAccount) => void;
  updateTokens: (token: string, refreshToken: string) => void;
  updateName: (name: string) => void;
  switchAccount: (tenantId: string) => void;
  removeAccount: (tenantId: string) => void;
  clearAuth: () => void;
  rehydrate: () => void;
}

function loadAccounts(): SavedAccount[] {
  try {
    const raw = localStorage.getItem(ACCOUNTS_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return [];
}

function saveAccounts(accounts: SavedAccount[]) {
  localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));
}

function loadActiveId(): string | null {
  try {
    const raw = localStorage.getItem(ACTIVE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
}

function saveActiveId(id: string | null) {
  if (id != null) {
    localStorage.setItem(ACTIVE_KEY, JSON.stringify(id));
  } else {
    localStorage.removeItem(ACTIVE_KEY);
  }
}

function initStore() {
  const accounts = loadAccounts();
  const activeId = loadActiveId();
  const active = accounts.find(a => a.tenant_id === activeId) ?? accounts[0] ?? null;

  return { accounts, active };
}

const { accounts: initialAccounts, active: initialActive } = initStore();

export const useAuthStore = create<AuthStore>()((set, get) => ({
  token: initialActive?.token ?? null,
  refreshToken: initialActive?.refresh_token ?? null,
  tenantId: initialActive?.tenant_id ?? null,
  email: initialActive?.email ?? null,
  name: initialActive?.name ?? null,
  isAuthenticated: !!initialActive?.token,
  accounts: initialAccounts,

  setAuth: (account) => {
    const accounts = get().accounts.filter(a => a.tenant_id !== account.tenant_id);
    accounts.unshift(account);
    saveAccounts(accounts);
    saveActiveId(account.tenant_id);
    set({
      token: account.token,
      refreshToken: account.refresh_token,
      tenantId: account.tenant_id,
      email: account.email,
      name: account.name ?? null,
      isAuthenticated: true,
      accounts,
    });
  },

  updateTokens: (token, refreshToken) => {
    const { tenantId, accounts } = get();
    const updated = accounts.map(a =>
      a.tenant_id === tenantId ? { ...a, token, refresh_token: refreshToken } : a
    );
    saveAccounts(updated);
    set({ token, refreshToken, isAuthenticated: true, accounts: updated });
  },

  updateName: (name) => {
    const { tenantId, accounts } = get();
    const updated = accounts.map(a =>
      a.tenant_id === tenantId ? { ...a, name } : a
    );
    saveAccounts(updated);
    set({ name, accounts: updated });
  },

  switchAccount: (tenantId) => {
    const account = get().accounts.find(a => a.tenant_id === tenantId);
    if (!account) return;
    saveActiveId(tenantId);
    set({
      token: account.token,
      refreshToken: account.refresh_token,
      tenantId: account.tenant_id,
      email: account.email,
      name: account.name ?? null,
      isAuthenticated: true,
    });
  },

  removeAccount: (tenantId) => {
    const { accounts, tenantId: currentId } = get();
    const remaining = accounts.filter(a => a.tenant_id !== tenantId);
    saveAccounts(remaining);

    if (currentId === tenantId) {
      // Removed active account — switch to next or clear
      const next = remaining[0];
      if (next) {
        saveActiveId(next.tenant_id);
        set({
          token: next.token,
          refreshToken: next.refresh_token,
          tenantId: next.tenant_id,
          email: next.email,
          name: next.name ?? null,
          isAuthenticated: true,
          accounts: remaining,
        });
      } else {
        saveActiveId(null);
        set({
          token: null, refreshToken: null, tenantId: null,
          email: null, name: null, isAuthenticated: false,
          accounts: [],
        });
      }
    } else {
      set({ accounts: remaining });
    }
  },

  clearAuth: () => {
    // True sign-out: drop current session immediately so ProtectedRoute
    // can redirect to /login. Other saved accounts remain available so the
    // user can pick them on the login screen, but the current session is
    // not silently swapped to a different account whose tokens may also
    // be dead — that masked failures and stranded users on blank screens.
    const { tenantId, accounts } = get();
    const remaining = accounts.filter(a => a.tenant_id !== tenantId);
    saveAccounts(remaining);
    saveActiveId(null);
    set({
      token: null,
      refreshToken: null,
      tenantId: null,
      email: null,
      name: null,
      isAuthenticated: false,
      accounts: remaining,
    });
  },

  // Re-read auth state from localStorage. Called when another tab mutates
  // the auth keys so all tabs converge on the same active session.
  rehydrate: () => {
    const accounts = loadAccounts();
    const activeId = loadActiveId();
    const active = accounts.find(a => a.tenant_id === activeId) ?? null;
    set({
      token: active?.token ?? null,
      refreshToken: active?.refresh_token ?? null,
      tenantId: active?.tenant_id ?? null,
      email: active?.email ?? null,
      name: active?.name ?? null,
      isAuthenticated: !!active?.token,
      accounts,
    });
  },
}));

// Cross-tab sync: when another tab mutates auth localStorage, mirror it here.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === ACCOUNTS_KEY || e.key === ACTIVE_KEY || e.key === null) {
      useAuthStore.getState().rehydrate();
    }
  });
}
